import { motion } from "framer-motion";
import { heroImageMotion } from "@/utils/motionVariants";

/**
 * HeroVisualCard — lightweight icon-based hero visual for product/about pages
 * that don't ship a dedicated hero photo.
 *
 * Renders on the right column of the 2-column hero layout. Hidden below lg so
 * mobile heroes stay single-column. Fades in with the shared `heroImageMotion`
 * variant to stay consistent with CardMachinesPage / EposSystemsPage heroes.
 *
 * @param {React.ComponentType} icon           Primary Lucide icon (centred, large).
 * @param {React.ComponentType[]} accentIcons  Up to 3 secondary icons for pill row.
 * @param {string[]} accentLabels              Labels for each secondary icon.
 * @param {string} testId
 */
export default function HeroVisualCard({ icon: Icon, accentIcons = [], accentLabels = [], testId = "hero-visual-card" }) {
  return (
    <motion.div
      {...heroImageMotion}
      className="hidden lg:flex flex-col items-center justify-center gap-6 w-full max-w-md mx-auto rounded-3xl bg-gradient-to-br from-brand-aqua-light via-white to-brand-pink-light/40 border border-brand-aqua/20 shadow-xl p-10 min-h-[380px]"
      data-testid={testId}
    >
      {Icon && (
        <div className="relative">
          <div className="absolute inset-0 bg-brand-aqua/20 blur-2xl rounded-full scale-110" aria-hidden="true" />
          <div className="relative w-28 h-28 rounded-3xl bg-white shadow-lg flex items-center justify-center float-animation">
            <Icon size={56} className="text-brand-aqua" strokeWidth={1.5} />
          </div>
        </div>
      )}
      {accentIcons.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
          {accentIcons.slice(0, 3).map((AccentIcon, i) => (
            <div
              key={accentLabels[i] || i}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-brand-aqua/15 rounded-full px-4 py-2 shadow-sm"
            >
              <AccentIcon size={14} className="text-brand-aqua flex-shrink-0" />
              <span className="text-xs font-semibold text-brand-black whitespace-nowrap">{accentLabels[i] || ""}</span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
